import { cn } from '../../../lib/utils';

export interface SegmentOption {
  key: string;
  label: string;
}

/** Pill-shaped segmented control — the upload-sheet prototype's `.seg`: one
 *  connected group of choices with dividers between them and the active one
 *  filled, replacing a row of separate outline buttons. The standard action
 *  control for the sheet-import review cards (roster conflicts, needs-fixing,
 *  duplicates). `value` matches an option key, or '' when none is selected
 *  (a pure action group with no persistent selection). */
export function SegmentedControl({
  options,
  value,
  onChange,
  disabled,
  ariaLabel,
  size = 'sm',
}: {
  options: SegmentOption[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  /** 'sm' (default) = 32px tall; 'xs' = 28px, a touch more compact. */
  size?: 'sm' | 'xs';
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex overflow-hidden rounded-full border border-border bg-card">
      {options.map((opt, i) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(opt.key)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 text-caption-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
              size === 'xs' ? 'h-7' : 'h-8',
              i > 0 && 'border-l border-border',
              active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
